import { ReactElement, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import { InboxOutlined } from "@ant-design/icons";
import {
  Upload,
  UploadProps,
  Table,
  Modal,
} from "antd";
import { ReactSVG } from "react-svg";
import { ColumnsType } from "antd/es/table";
import CustomTable from "../../components/custom-table/custom-table";
import viewImage from "../../assets/view.svg";

import clsx from "clsx";
import { Link } from "react-router-dom";
import CustomModal from "../../components/custom-modal/custom-modal";
import ViewUser from "../backend-users/components/view-user/view-user";
import toast from "react-hot-toast";
import UploadCustomersService from "./upload-customers.service";
import moment from "moment";
import { CREATE_BREADCRUMB } from "src/store/actions";

const { Dragger } = Upload;

const UploadCustomers: React.FC = (): ReactElement => {
  const dispatch = useDispatch();
  const uploadCustomersService = new UploadCustomersService(dispatch);
  const [onboardingList, setOnboardingList] = useState<any[]>([]);
  const [extractedData, setExtractedData] = useState<any[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch({
      type: CREATE_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: `Customers list`,
            type: "link",
            link: "/home/customers-list",
          },
        ],
      },
    });
  }, []);

  useEffect(() => {
    fetchOnboardingListData();
  }, []);

  const fetchOnboardingListData = async () => {
    setLoading(true);
    try {
      const data = await uploadCustomersService.getOnboardingListData();
      if (data) {
        setOnboardingList(data);
      }
    } catch (error) {
      console.error("Error fetching onboarding list data:", error);
    } finally {
      setLoading(false);
    }
  };

  const [openViewModal, setOpenViewModal] = useState(false);

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Uploaded by",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      fixed: "left",
      render: (text, record) => <span>{record?.admin?.name}</span>,
    },
    {
      title: "Total records",
      dataIndex: "totalUsers",
      key: "totalUsers",
    },
    {
      title: "File status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <span
          className={clsx(
            "text-xs font-semibold py-1 px-2 rounded-full",
            text === "Pending"
              ? "bg-[#E8D286] text-[#787523]"
              : "bg-green-700 text-white"
          )}
        >
          {text === "Done" ? "Completed" : "Pending"}
        </span>
      ),
    },
    {
      title: "Created at",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => moment(text).format("MMM D, YYYY hh:mm A"),
    },

    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="actions flex align-center">
          <Link
            // to={
            //   record.status === "Complete"
            //     ? "/customers/view"
            //     : "/customers/execute"
            // }
            to={`/customers/view/${record?.id}`}
          >
            <ReactSVG src={viewImage} />
          </Link>
          {/* <ReactSVG src={downloadImage} />
          <ReactSVG src={executeImage} /> */}
        </div>
      ),
    },
  ];

  const beforeUpload = async (file: File) => {
    const isExcel =
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls") ||
      file.name.endsWith(".csv");

    if (!isExcel) {
      toast.error(
        "Error: Only Excel files (.xlsx, .xls) are allowed for upload. Please try again with a valid file format!"
      );
      return Upload.LIST_IGNORE;
    }

    // Store the uploaded file and automatically extract Excel data
    if (isExcel) {
      setUploadedFile(file);
      try {
        const data = await uploadCustomersService.extractExcelDataAsJson(file);
        setExtractedData(data);
      } catch (error) {
        toast.error("Failed to extract data from Excel file");
        console.error("Excel extraction error:", error);
      }
    }

    return false; // Prevent automatic upload
  };

  const handleUploadData = async () => {
    if (extractedData.length === 0) {
      toast.error("Please extract Excel data first");
      return;
    }

    // Validate data structure
    if (extractedData.length > 0) {
      const firstRow = extractedData[0];

      // Check if required fields exist based on the template
      const requiredFields = [
        "accountCodes",
        "firstName",
        "lastName",
        "email",
        "phoneNumber",
        "password",
      ];
      const missingFields = requiredFields.filter(
        (field) => !(field in firstRow)
      );

      if (missingFields.length > 0) {
        console.warn("Missing required fields in data:", missingFields);
        toast.error(
          `Missing required fields: ${missingFields.join(
            ", "
          )}. Please use the template provided.`
        );
        return;
      }
    }

    try {
      const success = await uploadCustomersService.uploadExcelData(
        extractedData
      );
      if (success) {
        setExtractedData([]);
        setUploadedFile(null);
        setPreviewModalVisible(false);
        // Refresh the onboarding list
        const data = await uploadCustomersService.getOnboardingListData();
        if (data) {
          setOnboardingList(data);
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload failed. Please check the console for details.");
    }
  };

  const getPreviewColumns = (): ColumnsType<any> => {
    if (extractedData.length === 0) return [];

    const firstRow = extractedData[0];
    return Object.keys(firstRow).map((key) => ({
      title: key,
      dataIndex: key,
      key: key,
      render: (text: any) => (
        <span className="text-sm">
          {text !== null && text !== undefined ? String(text) : ""}
        </span>
      ),
    }));
  };

  const props: UploadProps = {
    name: "file",
    multiple: false,
    beforeUpload,
    onChange(info) {
      const { status } = info.file;
      if (status !== "uploading") {
        console.log(info.file, info.fileList);
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  return (
    <section className="flex flex-col">
      <BreadcrumbComponent />

      <div className=" flex flex-col bg-white p-5 rounded-lg border-[1px] border-[#ECECEC]">
        <h3 className="bg-black text-base font-bold border-b-[1px] border-[#D9D9D9] pb-4 mb-6">
          Upload accounts data
        </h3>

        {/* Template Download Section */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h4 className="text-blue-800 font-semibold mb-2 flex items-center">
                Excel Template Required
              </h4>
            </div>
            <a
              href="https://kamcoprod.blob.core.windows.net/system-container/Clients_Onboarding_template.csv"
              download="Clients_Onboarding_template.csv"
              className="ml-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center transition-colors duration-200"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download Template
            </a>
          </div>
        </div>

        <h4 className="text-[#2A2A2A] text-sm font-semibold mb-2">Files</h4>
        <Dragger
          {...props}
          style={{ background: "transparent" }}
          maxCount={1}
          itemRender={() => false}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Support for Excel files (.xlsx, .xls, .csv). For Excel files, data
            will be extracted as JSON.
          </p>
        </Dragger>

        {uploadedFile && (
          <div className="flex gap-2 mt-5">
            {extractedData.length > 0 && (
              <div className="flex items-center justify-end gap-7 w-full">
                <div className="flex items-center gap-7 ">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewModalVisible(true);
                    }}
                    className="bg-white border-[1px] border-blue-500 text-sm text-blue-500 px-5 py-2 rounded-lg w-fit whitespace-nowrap flex items-center justify-center"
                  >
                    Preview Data ({extractedData.length} rows)
                  </button>
                  <button
                    type="button"
                    onClick={handleUploadData}
                    className="bg-blue-500 text-white px-5 py-2 rounded-lg text-base"
                  >
                    Upload
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="users-table">
        <div className="title flex items-center justify-between">
          <p>Onboarding list</p>
          {/* <div className="images flex items-center">
            <CustomSearch />
            <Tooltip placement="topLeft" title="Columns settings">
              <img src={"/images/grid.svg"} alt="grid" />
            </Tooltip>
            <Tooltip placement="topLeft" title="Export">
              <img src={"/images/export.svg"} alt="export-customers" />
            </Tooltip>
          </div> */}
        </div>
        <CustomTable
          columns={columns}
          data={onboardingList}
          showPagination={false}
        />
      </div>

      {/* Preview Modal for Excel Data */}
      <Modal
        title={`Preview Excel Data (${extractedData.length} rows)`}
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        width={1200}
        footer={[
          <div className="flex items-center justify-end gap-3">
            <button
              key="cancel"
              className="bg-white border-[1px] border-blue-500 text-sm text-blue-500 px-5 py-2 rounded-lg w-fit whitespace-nowrap flex items-center justify-center"
              onClick={() => setPreviewModalVisible(false)}
            >
              Cancel
            </button>
            ,
            <button
              key="upload"
              onClick={handleUploadData}
              className="bg-blue-500 text-white px-5 py-2 rounded-lg text-base"
            >
              Upload Data
            </button>
          </div>,
        ]}
      >
        <div className="max-h-96 overflow-auto my-5">
          <Table
            columns={getPreviewColumns()}
            dataSource={extractedData.map((item, index) => ({
              ...item,
              key: index,
            }))}
            pagination={false}
            scroll={{ x: true }}
            size="small"
          />
        </div>
      </Modal>

      <CustomModal
        isVisible={openViewModal}
        centered
        width={900}
        showClose={false}
        onClose={() => {
          setOpenViewModal(false);
        }}
        className="edit-modal"
      >
        <div className="flex flex-col items-center justify-center">
          <ViewUser
            onCancel={() => {
              setOpenViewModal(false);
            }}
          />
        </div>
      </CustomModal>
    </section>
  );
};

export default UploadCustomers;
